import { Users, Code, Map, Palette, BookOpen } from "lucide-react";
import MatrixRain from "@/components/MatrixRain";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingActions from "@/components/FloatingActions";
import TechStack from "@/components/TechStack";
import { team, studioInfo } from "@/data/gamedev-data";

const About = () => {
  return (
    <div className="min-h-screen bg-background relative">
      <MatrixRain />
      <Navigation />

      {/* Hero Section */}
      <section className="relative py-20 pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 cyber-gradient opacity-20"></div>
        <div className="absolute inset-0 hex-pattern"></div>
        
        {/* ASCII Art Background */}
        <div className="absolute inset-0 flex items-center justify-center opacity-30 pointer-events-none scale-150">
          <pre className="font-terminal text-xs text-primary leading-tight">
{`
   █████╗ ██████╗  ██████╗ ██╗   ██╗████████╗
  ██╔══██╗██╔══██╗██╔═══██╗██║   ██║╚══██╔══╝
  ███████║██████╔╝██║   ██║██║   ██║   ██║   
  ██╔══██║██╔══██╗██║   ██║██║   ██║   ██║   
  ██║  ██║██████╔╝╚██████╔╝╚██████╔╝   ██║   
  ╚═╝  ╚═╝╚═════╝  ╚═════╝  ╚═════╝    ╚═╝   
`}
          </pre>
        </div>
        
        <div className="container mx-auto px-4 max-w-5xl relative z-10">
          <div className="text-center mb-8">
            <p className="text-sm font-mono text-primary/50 mb-6 tracking-widest uppercase">ABOUT</p>
            <h1 className="text-6xl md:text-7xl font-bold font-cyber mb-6 mt-32">
              <span className="text-primary">&lt;</span>
              <span className="neon-gradient bg-clip-text text-transparent">
                О_СТУДИИ
              </span>
              <span className="text-primary">/&gt;</span>
            </h1>
            <p className="font-mono text-foreground/70 text-lg mb-4">
              <span className="text-accent">{'// '}</span>Кто мы и чем занимаемся
            </p>
            <p className="font-terminal text-primary animate-pulse text-xl">
              {'> '} НЕЗАВИСИМАЯ_ИНДИ_СТУДИЯ {'_'}
            </p>
          </div>
        </div>
      </section>

      {/* Studio Info Section */}
      <section className="relative py-10">
        <div className="container mx-auto px-4 max-w-5xl relative z-10">
          <div className="cyber-card p-10 md:p-16 space-y-10">
            
            <div className="space-y-6 font-mono text-foreground/80 leading-relaxed text-base">
              <div className="cyber-card p-6 bg-background/50">
                <p className="text-primary font-bold mb-3 text-lg">{'>> '}{studioInfo.name}</p>
                <p>{studioInfo.description}</p>
              </div>
              
              <div className="cyber-card p-6 bg-background/50">
                <p className="text-accent font-bold mb-3 text-lg">{'>> НАША_МИССИЯ'}</p>
                <p>{studioInfo.mission}</p>
              </div>
              
              <div className="cyber-card p-6 bg-background/50">
                <p className="text-secondary font-bold mb-3 text-lg">{'>> ФИЛОСОФИЯ_РАЗРАБОТКИ'}</p>
                <div className="space-y-3">
                  <p>
                    <span className="text-primary">{'> '}</span>{studioInfo.philosophy.ideas}
                  </p>
                  <p>
                    <span className="text-accent">{'> '}</span>{studioInfo.philosophy.engine}
                  </p>
                  <p>
                    <span className="text-secondary">{'> '}</span>{studioInfo.philosophy.art}
                  </p>
                  <p>
                    <span className="text-primary">{'> '}</span>{studioInfo.philosophy.variety}
                  </p>
                </div>
              </div>
              
              {/* TechStack Component */}
              <TechStack />
              
              <div className="cyber-card p-6 bg-background/50">
                <p className="text-accent font-bold mb-3 text-lg">{'>> ТЕХНОЛОГИИ'}</p>
                <div className="space-y-3">
                  <div className="text-base">
                    <span className="text-primary">&gt;</span> Unity Engine - основной движок разработки
                  </div>
                  <div className="text-base">
                    <span className="text-secondary">&gt;</span> Собственные спрайты и ассеты
                  </div>
                  <div className="text-base">
                    <span className="text-accent">&gt;</span> 2D и 3D игровые миры
                  </div>
                  <div className="text-base">
                    <span className="text-primary">&gt;</span> Кастомные инструменты разработки
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 relative hex-pattern">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-card/20 to-background"></div>
        <div className="container mx-auto px-4 max-w-5xl relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold font-cyber text-center mb-12">
            <span className="text-primary">&lt;</span>
            <span className="terminal-text">КОМАНДА</span>
            <span className="text-primary">/&gt;</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {team.map((member) => {
              const IconComponent = 
                member.icon === "Code" ? Code :
                member.icon === "Map" ? Map :
                member.icon === "Palette" ? Palette :
                member.icon === "BookOpen" ? BookOpen : Users;

              const colorClass = 
                member.color === "primary" ? "border-primary/50 bg-gradient-to-br from-primary/20 to-primary/5" :
                member.color === "secondary" ? "border-secondary/50 bg-gradient-to-br from-secondary/20 to-secondary/5" :
                member.color === "accent" ? "border-accent/50 bg-gradient-to-br from-accent/20 to-accent/5" :
                "border-primary/50 bg-gradient-to-br from-primary/15 to-accent/5";

              const iconColorClass =
                member.color === "primary" ? "text-primary" :
                member.color === "secondary" ? "text-secondary" :
                member.color === "accent" ? "text-accent" :
                "text-primary";

              return (
                <div 
                  key={member.id} 
                  className={`cyber-card p-8 space-y-6 group transition-all duration-300 ${colorClass}`}
                >
                  <div className="flex items-start gap-6">
                    <div className={`p-3 rounded-sm border ${member.color === "primary" ? "border-primary" : member.color === "secondary" ? "border-secondary" : member.color === "accent" ? "border-accent" : "border-primary"} bg-background/50 transition-all duration-300`}>
                      <IconComponent className={`w-12 h-12 ${iconColorClass} transition-colors`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold font-cyber terminal-text mb-2 group-hover:text-accent transition-colors">
                        {member.name}
                      </h3>
                      <p className={`text-base font-mono ${iconColorClass} mb-3 font-bold`}>
                        {member.role}
                      </p>
                    </div>
                  </div>
                  
                   <p className="text-foreground/80 font-mono text-base leading-relaxed">
                    {member.description}
                  </p>
                  
                  <div className="space-y-2">
                    <p className="font-mono text-xs text-primary/80 mb-2">
                      {'>> НАВЫКИ:'}
                    </p>
                    {member.skills.slice(0, 4).map((skill, i) => (
                      <div key={i} className="text-base font-mono text-foreground/70">
                        <span className="text-secondary">&gt;</span> {skill}
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-1 pt-2">
                    <div className="h-1 flex-1 bg-primary/30"></div>
                    <div className="h-1 flex-1 bg-accent/30"></div>
                    <div className="h-1 flex-1 bg-secondary/30"></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <FloatingActions />
      <Footer />
    </div>
  );
};

export default About;
